import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Grid,
    Box,
    Card,
    List,
    ListItem,
    ListItemIcon,
    Button,
    CircularProgress,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import { useAppDispatch } from "../../Redux/Store/Hooks";
import { useNavigate } from "react-router-dom";
import { AddSubscription } from "../../Redux/Slices/Subscription/AddSubscriptionSlice";
import { listSubscription } from "../../Redux/Slices/Subscription/ListSubscriptionSlice";
import "./PricingDialog.scss";

interface PricingDialogProps {
    open: boolean;
    onClose: () => void;
    isUCAT: boolean;
    Packages: any[];
    isSubscriptionProcessing: boolean;
    setPricingChecked: (checked: boolean) => void;
}

const PricingDialog: React.FC<PricingDialogProps> = ({
    open,
    onClose,
    isUCAT,
    Packages,
    isSubscriptionProcessing,
    setPricingChecked,
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleMaybeLater = () => {
        onClose();
        setPricingChecked(true);
        const sleepDurationStr = process.env.REACT_APP_MAYBE_LATER_SLEEP_DURATION;
        const sleepDuration = sleepDurationStr ? parseInt(sleepDurationStr) * 1000 : 3600000; // Default 1 hour
        sessionStorage.setItem("clickedMaybeLater", (Date.now() + sleepDuration).toString());
    };

    const handleSubscribe = (pkg: any) => {
        sessionStorage.setItem("selectedPackage", JSON.stringify(pkg));
        dispatch(AddSubscription());
        dispatch(
            listSubscription({
                package_id: pkg.package_id,
            })
        );
    };

    const handleClose = () => {
        onClose();
        setPricingChecked(true);
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: { xs: "12px", md: "20px" },
                    padding: "0px 0px 20px 0px",
                    overflow: "visible",
                    backgroundColor: "#F8F9FA",
                    margin: { xs: "10px", sm: "32px" },
                },
            }}
        >
            <DialogTitle sx={{ padding: "0px" }}>
                <div
                    style={{
                        backgroundColor: "#1196f4",
                        borderRadius: "12px 12px 0px 0px",
                        padding: "20px 15px",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "flex-end",
                            marginTop: "-8px",
                        }}
                    >
                        <ClearIcon
                            sx={{ cursor: "pointer", color: "white" }}
                            onClick={handleClose}
                        />
                    </div>
                    <Typography
                        textAlign="center"
                        color="white"
                        sx={{
                            color: "white",
                            fontSize: { xs: "22px", sm: "28px", md: "34px" },
                            fontWeight: 600,
                            lineHeight: 1.2,
                            mb: 1,
                        }}
                    >
                        {isUCAT
                            ? "Prepare for UCAT with the Right support"
                            : "Start Free. Upgrade Anytime."}
                    </Typography>
                    <Typography
                        variant="body2"
                        textAlign="center"
                        sx={{
                            color: "#dbeafe",
                            fontSize: { xs: "14px", sm: "17px", md: "20px" },
                            fontWeight: 400,
                        }}
                    >
                        {isUCAT
                            ? "Choose the package that fits your stage"
                            : "Choose the plan that works best for your preparation needs"}
                    </Typography>
                </div>
            </DialogTitle>
            <DialogContent
                className="table-container"
                sx={{
                    padding: "0px",
                    backgroundColor: "white",
                    borderRadius: "20px 20px 0px 0px",
                }}
            >
                <Grid
                    item
                    xs={12}
                    md={12}
                    justifyContent="center"
                    sx={{
                        margin: { xs: "15px", sm: "20px auto" },
                        width: "100%",
                        maxWidth: "1000px",
                    }}
                >
                    <Box
                        className="cards-scroll-container"
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            flexWrap: "nowrap",
                            gap: "20px",
                            justifyContent: "flex-start",
                            alignItems: "stretch",
                            px: { xs: 2, md: "20px" },
                        }}
                    >
                        {(Array.isArray(Packages) ? [...Packages] : [])
                            .sort((a: any, b: any) => (a.user_cost ?? a.cost) - (b.user_cost ?? b.cost))
                            .map((pack: any) => (
                                <Box
                                    sx={{
                                        flex: "0 0 auto",
                                        width: {
                                            xs: "calc(100% - 30px)", // 1 card on mobile
                                            sm: "calc(50% - 20px)",  // 2 cards on tablet/desktop
                                            md: "calc(50% - 20px)",
                                        },
                                        scrollSnapAlign: "start",
                                    }}
                                    key={pack.package_id}
                                >
                                    <div
                                        className={`${pack.recommended ? "line-cardpopup" : ""}`}
                                        style={
                                            !pack.recommended
                                                ? {
                                                    background: "#e0e0e0",
                                                    padding: "1px",
                                                    borderRadius: "10px",
                                                    height: "100%",
                                                }
                                                : { height: "100%" }
                                        }
                                    >
                                        <Card
                                            className="card-containerpopup"
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                height: "100%",
                                                justifyContent: "space-between",
                                                position: "relative",
                                            }}
                                        >
                                            {pack.recommended && (
                                                <Box
                                                    sx={{
                                                        position: "absolute",
                                                        top: "17px",
                                                        right: "10px",
                                                        background: " #6366f1",
                                                        color: "white",
                                                        px: 2.3,
                                                        fontSize: "1rem",
                                                        fontWeight: "bold",
                                                        borderRadius: "15px",
                                                        zIndex: 1,
                                                    }}
                                                >
                                                    RECOMMENDED
                                                </Box>
                                            )}

                                            <Box>
                                                <Typography
                                                    variant="h5"
                                                    component="h3"
                                                    sx={{
                                                        fontWeight: "700",
                                                        fontSize: "24px",
                                                        lineHeight: "32px",
                                                        color: "black",
                                                        marginTop: "20px",
                                                    }}
                                                >
                                                    {pack.package_name}
                                                </Typography>
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        fontWeight: "700",
                                                        fontSize: "36px",
                                                        lineHeight: "44px",
                                                        color: "black",
                                                    }}
                                                >
                                                    ${pack.user_cost ?? pack.cost}
                                                    {(pack.user_cost ?? pack.cost) === 0 ? (
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: "17px",
                                                                fontWeight: "bold",
                                                                color: "#374151",
                                                                marginLeft: "10px",
                                                            }}
                                                        >
                                                            Forever
                                                        </Typography>
                                                    ) : (
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: "17px",
                                                                fontWeight: "700",
                                                                lineHeight: "24px",
                                                                color: "#374151",
                                                                marginLeft: "10px",
                                                            }}
                                                        >
                                                            per week, billed{" "}
                                                            {pack.billing_type?.toLowerCase() || "monthly"}
                                                        </Typography>
                                                    )}
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        mb: 2,
                                                        fontWeight: "400",
                                                        color: "#374151",
                                                        fontSize: "17px",
                                                        lineHeight: "26px",
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                            fontSize: "17px",
                                                        }}
                                                    >
                                                        {pack.description}
                                                    </span>
                                                </Typography>
                                                <Box sx={{ mb: 4, mt: 2 }}>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: "black",
                                                            fontSize: "16px",
                                                            marginBottom: "10px",
                                                            marginTop: "30px",
                                                        }}
                                                    >
                                                        What's included:
                                                    </Typography>
                                                    <List dense sx={{ p: 0 }}>
                                                        {pack.features &&
                                                            [...pack.features]
                                                                .sort(
                                                                    (a: any, b: any) =>
                                                                        Number(b.status) - Number(a.status)
                                                                )
                                                                .map((feature: any, idx: number) => (
                                                                    <ListItem
                                                                        key={idx}
                                                                        disablePadding
                                                                        sx={{ marginBottom: "10px" }}
                                                                    >
                                                                        <ListItemIcon sx={{ minWidth: "30px" }}>
                                                                            {feature.status ? (
                                                                                <CheckIcon sx={{ color: "green" }} />
                                                                            ) : (
                                                                                <Box
                                                                                    sx={{ color: "text.disabled", mr: 1 }}
                                                                                >
                                                                                    <ClearIcon />
                                                                                </Box>
                                                                            )}
                                                                        </ListItemIcon>
                                                                        <Typography
                                                                            sx={{
                                                                                color: feature.status
                                                                                    ? "black"
                                                                                    : "text.disabled",
                                                                                fontSize: "16px",
                                                                                textDecoration: feature.status
                                                                                    ? "none"
                                                                                    : "line-through",
                                                                            }}
                                                                        >
                                                                            {feature.name}
                                                                        </Typography>
                                                                    </ListItem>
                                                                ))}
                                                    </List>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mt: "auto" }}>
                                                {(pack.user_cost ?? pack.cost) > 0 ? (
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        size="large"
                                                        sx={{
                                                            fontSize: {
                                                                xs: "1rem",
                                                                sm: "1.1rem",
                                                                md: "1.2rem",
                                                            },
                                                            background: "#6366f1",
                                                            color: "white",
                                                            padding: { xs: "8px", sm: "10px" },
                                                            fontWeight: "bold",
                                                            textTransform: "capitalize",
                                                            borderRadius: "10px",
                                                            transition: "all 0.3s ease",
                                                            width: "100%",
                                                            minWidth: 0,
                                                            maxWidth: "100%",
                                                            boxSizing: "border-box",
                                                            "&:hover": {
                                                                background: "#6366f1",
                                                                color: "white",
                                                            },
                                                        }}
                                                        onClick={() => handleSubscribe(pack)}
                                                    >
                                                        {isSubscriptionProcessing
                                                            ? "Processing..."
                                                            : "Subscribe"}
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        size="large"
                                                        sx={{
                                                            fontSize: "1.2rem",
                                                            background: "#009DFF",
                                                            color: "white",
                                                            padding: "10px",
                                                            fontWeight: "bold",
                                                            textTransform: "capitalize",
                                                            borderRadius: "10px",
                                                            width: "100%",
                                                            "&:hover": {
                                                                background: "#009DFF",
                                                                color: "white",
                                                            },
                                                        }}
                                                        onClick={() => {
                                                            navigate("/practicetest");
                                                            onClose();
                                                        }}
                                                    >
                                                        Start Free
                                                    </Button>
                                                )}
                                            </Box>
                                        </Card>
                                    </div>
                                </Box>
                            ))}
                    </Box>
                </Grid>
            </DialogContent>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    pt: 2,
                }}
            >
                <div
                    className="pricingmaybelater"
                    style={{
                        color: "#4b5563",
                        cursor: "pointer",
                        fontSize: "16px",
                        fontWeight: 500,
                        padding: "10px",
                    }}
                    onClick={handleMaybeLater}
                >
                    Maybe Later
                </div>
                {/* <Button
                    className="pricingmaybelater"
                    sx={{
                        color: "white",
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: "200px" },
                        fontWeight: "bold",
                        backgroundColor: isSubscriptionProcessing ? "grey" : "#009DFF",
                        cursor: "pointer",
                        padding: "12px 24px",
                        "&:hover": {
                            background: isSubscriptionProcessing ? "grey" : "#009DFF",
                            color: "white",
                        },
                    }}
                    fullWidth={false}
                    onClick={() => handleSubscribe(Packages[0]?.package_id)}
                >
                    {isSubscriptionProcessing ? (
                        <>
                            <CircularProgress size={24} color="inherit" />
                            <span style={{ marginLeft: "10px" }}>Please wait ...</span>
                        </>
                    ) : (
                        "Continue to Payment"
                    )}
                </Button> */}
            </DialogContent>
        </Dialog>
    );
};

export default PricingDialog;
